import { InputRule, type Editor } from "@tiptap/core";
import { BlockMath, InlineMath } from "@tiptap/extension-mathematics";
import type { Node as PMNode } from "@tiptap/pm/model";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import katex from "katex";
import "katex/dist/katex.min.css";
import { installMarkdownItMath } from "./markdownItMath";

/**
 * LaTeX math: `$latex$` inline and `$$…$$` block, rendered with KaTeX.
 *
 * Built on `@tiptap/extension-mathematics` (schema + commands) with three
 * Caliu-specific layers:
 *
 * - **Markdown round-trip** through tiptap-markdown (`addStorage`): the
 *   markdown-it rules in `markdownItMath.ts` parse `$…$` / `$$…$$` before
 *   any escape/emphasis rule can mangle the LaTeX, and `serialize` writes
 *   the source back verbatim. The delimiter contract lives in `@caliu/utils`
 *   and is mirrored by the Swift editor.
 * - **Editing**: math nodes are atoms. Clicking one (or pressing Enter with
 *   it selected) dispatches `MATH_EDIT_EVENT`; `MathEditorPopover` listens and
 *   shows a source field with live preview. Empty LaTeX on commit deletes
 *   the node, so an abandoned insert leaves nothing behind.
 * - **Input rules**: typing the closing `$` of `$latex$` converts to inline
 *   math; `$$` on its own line + Enter (or a one-line `$$latex$$`) makes a
 *   block.
 */

export const MATH_EDIT_EVENT = "caliu:edit-math";

export interface MathEditDetail {
  pos: number;
}

const KATEX_OPTIONS = { throwOnError: false } as const;

function renderLatex(el: HTMLElement, latex: string, displayMode: boolean) {
  if (!latex.trim()) {
    // Fresh node with no source yet: show a muted glyph so the user sees
    // where the expression will go (replaced as soon as they type).
    el.textContent = "∑";
    el.classList.add("caliu-math-empty");
    return;
  }
  el.classList.remove("caliu-math-empty");
  katex.render(latex, el, { ...KATEX_OPTIONS, displayMode });
}

export function requestMathEdit(editor: Editor, pos: number) {
  if (!editor.isEditable) return;
  window.dispatchEvent(
    new CustomEvent<MathEditDetail>(MATH_EDIT_EVENT, { detail: { pos } }),
  );
}

function mathNodeView(kind: "inline" | "block") {
  const displayMode = kind === "block";
  return ({
    node: initial,
    getPos,
    editor,
  }: {
    node: PMNode;
    getPos: () => number | undefined;
    editor: Editor;
  }) => {
    let node = initial;
    const dom = document.createElement(displayMode ? "div" : "span");
    dom.className = displayMode
      ? "caliu-math caliu-math-block"
      : "caliu-math caliu-math-inline";
    dom.setAttribute("data-type", displayMode ? "block-math" : "inline-math");
    dom.setAttribute("data-latex", String(node.attrs.latex ?? ""));
    dom.contentEditable = "false";
    renderLatex(dom, String(node.attrs.latex ?? ""), displayMode);

    const onClick = (event: Event) => {
      if (!editor.isEditable) return;
      event.preventDefault();
      const pos = getPos();
      if (pos == null) return;
      editor.commands.setNodeSelection(pos);
      requestMathEdit(editor, pos);
    };
    dom.addEventListener("click", onClick);

    return {
      dom,
      update(updated: PMNode) {
        if (updated.type !== node.type) return false;
        node = updated;
        const latex = String(node.attrs.latex ?? "");
        dom.setAttribute("data-latex", latex);
        renderLatex(dom, latex, displayMode);
        return true;
      },
      selectNode() {
        dom.classList.add("ProseMirror-selectednode");
      },
      deselectNode() {
        dom.classList.remove("ProseMirror-selectednode");
      },
      // KaTeX owns the subtree; ProseMirror must never re-parse it.
      ignoreMutation: () => true,
      destroy() {
        dom.removeEventListener("click", onClick);
      },
    };
  };
}

/** Enter on a selected math node opens the editor instead of splitting. */
function editSelectedMath(editor: Editor, typeName: string): boolean {
  const { selection } = editor.state;
  if (!(selection instanceof NodeSelection)) return false;
  if (selection.node.type.name !== typeName) return false;
  requestMathEdit(editor, selection.from);
  return true;
}

export const InlineMathExtension = InlineMath.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(
          state: { write: (s: string) => void },
          node: { attrs: { latex?: unknown } },
        ) {
          // Coerce, never trust: a throw here aborts getMarkdown() for the
          // whole note and kills autosave (see ImageExtension).
          const latex = String(node.attrs.latex ?? "").trim();
          if (!latex) return; // an empty node is transient — drop it
          state.write(`$${latex}$`);
        },
        parse: {
          setup: installMarkdownItMath,
        },
      },
    };
  },

  addInputRules() {
    return [
      new InputRule({
        // Closing `$` just typed; same delimiter rules as MATH_INLINE_REGEX.
        find: /(?<![\\$])\$(?![\s$])([^$\n]+?)(?<![\s\\])\$$/,
        handler: ({ state, range, match }) => {
          const $from = state.doc.resolve(range.from);
          if ($from.parent.type.spec.code) return;
          state.tr.replaceWith(
            range.from,
            range.to,
            this.type.create({ latex: match[1] }),
          );
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => editSelectedMath(this.editor, this.name),
    };
  },

  addNodeView() {
    return mathNodeView("inline");
  },
});

export const BlockMathExtension = BlockMath.extend({
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(
          state: { write: (s: string) => void; closeBlock: (n: unknown) => void },
          node: { attrs: { latex?: unknown } },
        ) {
          const latex = String(node.attrs.latex ?? "").trim();
          if (latex) state.write(`$$\n${latex}\n$$`);
          state.closeBlock(node);
        },
        parse: {
          setup: installMarkdownItMath,
        },
      },
    };
  },

  addInputRules() {
    return [
      new InputRule({
        // A whole line `$$latex$$` becomes a block.
        find: /^\$\$([^$\n]+?)\$\$$/,
        handler: ({ state, range, match }) => {
          const $from = state.doc.resolve(range.from);
          if (!$from.parent.isTextblock || range.from !== $from.start()) return;
          if (!$from.node(-1).canReplaceWith($from.index(-1), $from.indexAfter(-1), this.type)) return;
          state.tr.replaceWith(
            $from.before(),
            $from.after(),
            this.type.create({ latex: match[1].trim() }),
          );
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      Enter: () => {
        if (editSelectedMath(this.editor, this.name)) return true;
        // `$$` alone on a line + Enter → new block, editor open.
        const { $from, empty } = this.editor.state.selection;
        if (!empty || !$from.parent.isTextblock) return false;
        if ($from.parent.type.name !== "paragraph") return false;
        if ($from.parent.textContent.trim() !== "$$") return false;
        const pos = $from.before();
        this.editor
          .chain()
          .command(({ tr }) => {
            tr.replaceWith(pos, $from.after(), this.type.create({ latex: "" }));
            tr.setSelection(NodeSelection.create(tr.doc, pos));
            return true;
          })
          .run();
        requestMathEdit(this.editor, pos);
        return true;
      },
    };
  },

  addNodeView() {
    return mathNodeView("block");
  },
});

/**
 * Toolbar action: wrap the selected text as inline math (the selection is
 * the LaTeX source), or insert an empty inline node, then open the editor.
 */
export function insertInlineMathAndEdit(editor: Editor) {
  const { from, to, empty } = editor.state.selection;
  const type = editor.schema.nodes.inlineMath;
  if (!type) return;
  const latex = empty ? "" : editor.state.doc.textBetween(from, to, " ").trim();
  editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.replaceWith(from, to, type.create({ latex }));
      tr.setSelection(NodeSelection.create(tr.doc, from));
      return true;
    })
    .run();
  requestMathEdit(editor, from);
}

/**
 * Slash command: replace the current (empty) paragraph with a block math
 * node, or insert one after the current block, then open the editor.
 */
export function insertBlockMathAndEdit(editor: Editor) {
  const type = editor.schema.nodes.blockMath;
  if (!type) return;
  const { $from } = editor.state.selection;
  const inEmptyParagraph =
    $from.parent.type.name === "paragraph" && $from.parent.content.size === 0;
  const pos = inEmptyParagraph ? $from.before() : $from.after();
  editor
    .chain()
    .focus()
    .command(({ tr }) => {
      if (inEmptyParagraph) {
        tr.replaceWith($from.before(), $from.after(), type.create({ latex: "" }));
      } else {
        tr.insert(pos, type.create({ latex: "" }));
      }
      tr.setSelection(NodeSelection.create(tr.doc, pos));
      return true;
    })
    .run();
  requestMathEdit(editor, pos);
}

/** Move the caret right after the math node at `pos` (or to the doc end). */
export function placeCaretAfterMath(editor: Editor, pos: number) {
  const node = editor.state.doc.nodeAt(pos);
  const after = node ? pos + node.nodeSize : pos;
  const target = Math.min(after, editor.state.doc.content.size);
  editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.setSelection(TextSelection.near(tr.doc.resolve(target), 1));
      return true;
    })
    .run();
}
